/**
 * KbAiObservability — Painel Admin Root para monitorar consumo de IA da
 * Base de Conhecimento (KB): tokens, custo USD, latência, erros, buscas
 * e saúde das filas. Dados agregados globais e por tenant.
 *
 * Fonte: RPCs `kb_ai_obs_*` (SECURITY DEFINER, root_admin only).
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Coins,
  Timer,
  AlertTriangle,
  Search,
  RefreshCw,
  DollarSign,
  Sparkles,
  Building2,
  ChevronRight,
  Cpu,
  ListChecks,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
} from "@/components/layout/list-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PaginationFooter } from "@/components/ui/pagination-footer";
import {
  useKbAiObsKpis,
  useKbAiObsTimeseries,
  useKbAiObsByTenant,
  useKbAiObsByModel,
  useKbAiObsByPurpose,
  useKbAiObsRecentCalls,
  useKbAiObsCallDetail,
  useKbAiObsJobsHealth,
  useKbAiObsSearchStats,
  type KbAiObsFilters,
} from "@/hooks/admin/useKbAiObservability";

// ---------------------------------------------------------------------------
// Utilitários de formatação
// ---------------------------------------------------------------------------
const nfInt = new Intl.NumberFormat("pt-BR");
const nfUsd = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 4,
  maximumFractionDigits: 6,
});
const shortId = (id?: string | null) => (id ? id.slice(0, 8).toUpperCase() : "—");
const fmtNum = (n: number | string | null | undefined) =>
  n == null ? "—" : nfInt.format(Number(n));
const fmtUsd = (n: number | string | null | undefined) =>
  n == null ? "—" : nfUsd.format(Number(n));
const fmtMs = (n: number | null | undefined) =>
  n == null ? "—" : `${nfInt.format(Number(n))} ms`;

// Chart colors — tokens semânticos (respeitam tema)
const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

// Preset de período em dias
const PERIOD_OPTIONS = [
  { value: "7", label: "Últimos 7 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "90", label: "Últimos 90 dias" },
];

const PURPOSE_OPTIONS = [
  { value: "all", label: "Todas finalidades" },
  { value: "embedding", label: "Embedding" },
  { value: "rag_search", label: "Busca RAG" },
  { value: "rag_ask", label: "Pergunta RAG" },
  { value: "transcription", label: "Transcrição" },
  { value: "reindex", label: "Reindex" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "success", label: "Sucesso" },
  { value: "error", label: "Erro" },
  { value: "billing_exhausted", label: "Sem crédito" },
];

// Diferença percentual entre atual e período anterior
function pctDelta(curr: number, prev: number): string {
  if (!prev) return curr > 0 ? "novo" : "0%";
  const d = ((curr - prev) / prev) * 100;
  const sign = d > 0 ? "+" : "";
  return `${sign}${d.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Página raiz
// ---------------------------------------------------------------------------
export default function KbAiObservability() {
  const qc = useQueryClient();
  const [periodDays, setPeriodDays] = useState("30");
  const [purpose, setPurpose] = useState("all");
  const [tab, setTab] = useState("overview");

  // Intervalo derivado do período selecionado
  const { from, to } = useMemo(() => {
    const days = Number(periodDays);
    const toDate = new Date();
    const fromDate = new Date(toDate.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: fromDate.toISOString(), to: toDate.toISOString() };
  }, [periodDays]);

  const purposesFilter = purpose === "all" ? null : [purpose];
  const filters: KbAiObsFilters = { from, to, tenantIds: null, purposes: purposesFilter };

  const { data: kpis, isLoading: kpisLoading } = useKbAiObsKpis(filters);

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Observabilidade IA — Base de Conhecimento"
        description="Monitoramento de tokens, custo, latência e erros das chamadas de IA (embedding, busca, RAG, transcrição) do módulo Wiki"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ["admin", "kb-ai-obs"] })}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        }
      />

      <ListPageFiltersBar>
        <Select value={periodDays} onValueChange={setPeriodDays}>
          <SelectTrigger className={LIST_FILTERS_SELECT_TRIGGER_CLASS}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={purpose} onValueChange={setPurpose}>
          <SelectTrigger className={LIST_FILTERS_SELECT_TRIGGER_CLASS}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PURPOSE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageKpis>
        <KpiCard
          color="purple"
          icon={Sparkles}
          label="Chamadas"
          value={fmtNum(kpis?.current.calls)}
          suffix={
            kpis ? pctDelta(kpis.current.calls, kpis.previous.calls) : undefined
          }
        />
        <KpiCard
          color="blue"
          icon={Coins}
          label="Tokens totais"
          value={fmtNum(kpis?.current.total_tokens)}
          suffix={
            kpis
              ? pctDelta(kpis.current.total_tokens, kpis.previous.total_tokens)
              : undefined
          }
        />
        <KpiCard
          color="green"
          icon={DollarSign}
          label="Custo (USD)"
          value={fmtUsd(kpis?.current.cost_usd)}
          suffix={
            kpis
              ? pctDelta(
                  Number(kpis.current.cost_usd),
                  Number(kpis.previous.cost_usd),
                )
              : undefined
          }
        />
        <KpiCard
          color="cyan"
          icon={Timer}
          label="Latência média"
          value={fmtMs(kpis?.current.latency_avg_ms)}
          suffix={`p95 ${fmtMs(kpis?.current.latency_p95_ms)}`}
        />
        <KpiCard
          color="red"
          icon={AlertTriangle}
          label="Erros"
          value={fmtNum(kpis?.current.errors)}
          suffix={
            kpis && kpis.current.calls > 0
              ? `${((kpis.current.errors / kpis.current.calls) * 100).toFixed(1)}%`
              : "0%"
          }
        />
      </ListPageKpis>

      <ListPageContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Visão geral</TabsTrigger>
            <TabsTrigger value="tenants">Por tenant</TabsTrigger>
            <TabsTrigger value="models">Por modelo</TabsTrigger>
            <TabsTrigger value="calls">Requisições</TabsTrigger>
            <TabsTrigger value="health">Jobs &amp; buscas</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <OverviewTab filters={filters} />
          </TabsContent>
          <TabsContent value="tenants" className="mt-4">
            <TenantsTab from={from} to={to} purposes={purposesFilter} />
          </TabsContent>
          <TabsContent value="models" className="mt-4">
            <ModelsTab filters={filters} />
          </TabsContent>
          <TabsContent value="calls" className="mt-4">
            <CallsTab from={from} to={to} purposes={purposesFilter} />
          </TabsContent>
          <TabsContent value="health" className="mt-4">
            <HealthTab from={from} to={to} />
          </TabsContent>
        </Tabs>
      </ListPageContent>
    </ListPageLayout>
  );
}

// ---------------------------------------------------------------------------
// Aba: Visão geral (timeseries + donut por purpose)
// ---------------------------------------------------------------------------
function OverviewTab({ filters }: { filters: KbAiObsFilters }) {
  const { data: series = [] } = useKbAiObsTimeseries(filters);
  const { data: byPurpose = [] } = useKbAiObsByPurpose(
    filters.from,
    filters.to,
    filters.tenantIds ?? null,
  );

  // Pivot para gráfico stacked: dia × purpose(cost)
  const { chartData, purposes } = useMemo(() => {
    const map = new Map<string, Record<string, any>>();
    const purposesSet = new Set<string>();
    for (const row of series) {
      purposesSet.add(row.purpose);
      const cur = map.get(row.bucket_day) ?? { bucket_day: row.bucket_day };
      cur[row.purpose] = (cur[row.purpose] ?? 0) + Number(row.cost_usd);
      map.set(row.bucket_day, cur);
    }
    return {
      chartData: Array.from(map.values()).sort((a, b) =>
        a.bucket_day < b.bucket_day ? -1 : 1,
      ),
      purposes: Array.from(purposesSet),
    };
  }, [series]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Custo USD por dia (empilhado por finalidade)</CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          {chartData.length === 0 ? (
            <EmptyState message="Sem dados no período" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="bucket_day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RTooltip
                  formatter={(v: any) => nfUsd.format(Number(v))}
                  contentStyle={{
                    backgroundColor: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
                {purposes.map((p, i) => (
                  <Bar key={p} dataKey={p} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Distribuição por finalidade</CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          {byPurpose.length === 0 ? (
            <EmptyState message="Sem dados" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={byPurpose}
                  dataKey="cost_usd"
                  nameKey="purpose"
                  innerRadius={50}
                  outerRadius={90}
                >
                  {byPurpose.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(v: any, n: any) => [nfUsd.format(Number(v)), n]}
                  contentStyle={{
                    backgroundColor: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: Por tenant
// ---------------------------------------------------------------------------
function TenantsTab({
  from,
  to,
  purposes,
}: {
  from: string;
  to: string;
  purposes: string[] | null;
}) {
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const { data: rows = [], isLoading } = useKbAiObsByTenant(
    from,
    to,
    purposes,
    pageSize,
    page * pageSize,
  );
  const totalRows = rows[0]?.total_rows ?? 0;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead className="text-right">Chamadas</TableHead>
              <TableHead className="text-right">Erros</TableHead>
              <TableHead className="text-right">Tokens in/out</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Custo USD</TableHead>
              <TableHead className="text-right">Latência avg/p95</TableHead>
              <TableHead>Última chamada</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Nenhum consumo no período
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.tenant_id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <div>
                        <div className="font-medium text-sm">{r.tenant_name}</div>
                        <div className="text-xs text-muted-foreground">{shortId(r.tenant_id)}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(r.calls)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.errors > 0 ? (
                      <Badge variant="destructive">{fmtNum(r.errors)}</Badge>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {fmtNum(r.input_tokens)} / {fmtNum(r.output_tokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {fmtNum(r.total_tokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {fmtUsd(r.cost_usd)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {fmtMs(r.latency_avg_ms)} / {fmtMs(r.latency_p95_ms)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.last_call_at
                      ? formatDistanceToNow(new Date(r.last_call_at), {
                          addSuffix: true,
                          locale: ptBR,
                        })
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalRows > pageSize && (
          <PaginationFooter
            totalItems={totalRows}
            currentPage={page + 1}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p - 1)}
            onPageSizeChange={() => {}}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Aba: Por modelo
// ---------------------------------------------------------------------------
function ModelsTab({ filters }: { filters: KbAiObsFilters }) {
  const { data: rows = [], isLoading } = useKbAiObsByModel(filters);
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Modelo</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead className="text-right">Chamadas</TableHead>
              <TableHead className="text-right">Erros</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">Custo USD</TableHead>
              <TableHead className="text-right">Latência avg</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Nenhum uso registrado
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.model_id + r.provider_name}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-mono text-xs">{r.model_slug}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{r.provider_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(r.calls)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.errors > 0 ? (
                      <Badge variant="destructive">{fmtNum(r.errors)}</Badge>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(r.total_tokens)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {fmtUsd(r.cost_usd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMs(r.latency_avg_ms)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Aba: Requisições recentes (paginada + sheet de detalhe)
// ---------------------------------------------------------------------------
function CallsTab({
  from,
  to,
  purposes,
}: {
  from: string;
  to: string;
  purposes: string[] | null;
}) {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const pageSize = 50;

  const { data: rows = [], isLoading } = useKbAiObsRecentCalls({
    from,
    to,
    purposes,
    status: status === "all" ? null : status,
    search: search.trim() || null,
    limit: pageSize,
    offset: page * pageSize,
  });
  const totalRows = rows[0]?.total_rows ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por request_id ou erro…"
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
            className={`${LIST_FILTERS_INPUT_CLASS} pl-8`}
          />
        </div>
        <Select value={status} onValueChange={(v) => { setPage(0); setStatus(v); }}>
          <SelectTrigger className={LIST_FILTERS_SELECT_TRIGGER_CLASS}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quando</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Finalidade</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">USD</TableHead>
                <TableHead className="text-right">Latência</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Carregando…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Nenhuma requisição
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedId(r.id)}
                  >
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(r.created_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </TableCell>
                    <TableCell className="text-xs">{r.tenant_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {r.purpose}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{r.model_slug}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {fmtNum(r.total_tokens)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {fmtUsd(r.cost_usd)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {fmtMs(r.latency_ms)}
                    </TableCell>
                    <TableCell>
                      {r.status === "success" ? (
                        <Badge variant="secondary" className="text-[10px]">
                          ok
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-[10px]">
                          {r.status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {totalRows > pageSize && (
            <PaginationFooter
              totalItems={totalRows}
              currentPage={page + 1}
              pageSize={pageSize}
              onPageChange={(p) => setPage(p - 1)}
              onPageSizeChange={() => {}}
            />
          )}
        </CardContent>
      </Card>

      <CallDetailSheet
        callId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sheet: detalhe de uma chamada
// ---------------------------------------------------------------------------
function CallDetailSheet({
  callId,
  onClose,
}: {
  callId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useKbAiObsCallDetail(callId);

  return (
    <Sheet open={!!callId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Detalhe da chamada</SheetTitle>
          <SheetDescription>
            {callId ? shortId(callId) : ""}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3 text-sm">
          {isLoading || !data ? (
            <p className="text-muted-foreground">Carregando…</p>
          ) : (
            <>
              <DetailRow label="Tenant" value={data.tenant_name} />
              <DetailRow label="Finalidade" value={data.purpose} />
              <DetailRow label="Operação" value={data.operation} />
              <DetailRow label="Modelo" value={data.model_slug} mono />
              <DetailRow label="Provider" value={data.provider_name} />
              <DetailRow
                label="Tokens (in / out / total)"
                value={`${fmtNum(data.input_tokens)} / ${fmtNum(data.output_tokens)} / ${fmtNum(data.total_tokens)}`}
              />
              <DetailRow label="Custo USD" value={fmtUsd(data.cost_usd)} />
              <DetailRow label="Latência" value={fmtMs(data.latency_ms)} />
              <DetailRow label="Status" value={data.status} />
              {data.error_message && (
                <DetailRow label="Erro" value={data.error_message} />
              )}
              <DetailRow label="Request ID" value={data.request_id} mono />
              {data.page_title && (
                <DetailRow label="Página KB" value={data.page_title} />
              )}
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">
                  Metadata
                </div>
                <pre className="bg-muted rounded p-2 text-xs overflow-x-auto max-h-80">
                  {JSON.stringify(data.metadata ?? {}, null, 2)}
                </pre>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: any;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/50 pb-2">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      <span className={`text-right ${mono ? "font-mono text-xs" : ""}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: saúde das filas + estatísticas de busca
// ---------------------------------------------------------------------------
function HealthTab({ from, to }: { from: string; to: string }) {
  const { data: health } = useKbAiObsJobsHealth(null);
  const { data: searchStats } = useKbAiObsSearchStats(from, to, null);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ListChecks className="h-4 w-4" /> Filas KB
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <QueueRow name="Embedding" data={health?.embedding} />
            <QueueRow name="Transcrição" data={health?.transcription} />
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-sm font-medium">Total DLQ</span>
              <Badge variant={health?.dlq_total ? "destructive" : "secondary"}>
                {fmtNum(health?.dlq_total ?? 0)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Search className="h-4 w-4" /> Buscas no período
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <DetailRow label="Total de buscas" value={fmtNum(searchStats?.total_searches)} />
            <DetailRow label="Latência média" value={fmtMs(searchStats?.avg_latency_ms)} />
            <DetailRow label="Latência p95" value={fmtMs(searchStats?.p95_latency_ms)} />
            <DetailRow label="Resultados por busca (média)" value={searchStats?.avg_results ?? "—"} />
            <DetailRow
              label="Buscas sem resultado"
              value={`${searchStats?.zero_result_pct ?? 0}%`}
            />
            {searchStats?.by_type && (
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1 mt-2">
                  Por tipo
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(searchStats.by_type).map(([k, v]) => (
                    <Badge key={k} variant="outline">
                      {k}: {fmtNum(v as number)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QueueRow({
  name,
  data,
}: {
  name: string;
  data?: { pending: number; processing: number; failed: number; done_24h: number };
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium">{name}</span>
        <span className="text-xs text-muted-foreground">
          {fmtNum(data?.done_24h ?? 0)} concluídos 24h
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1 text-xs">
        <MiniStat label="Pendentes" value={data?.pending ?? 0} />
        <MiniStat label="Processando" value={data?.processing ?? 0} />
        <MiniStat label="Falhas" value={data?.failed ?? 0} tone={data?.failed ? "danger" : "default"} />
      </div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "danger";
}) {
  return (
    <div
      className={`rounded border px-2 py-1 ${
        tone === "danger" && value > 0
          ? "border-destructive/30 bg-destructive/10 text-destructive"
          : "border-border bg-muted/30"
      }`}
    >
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="tabular-nums font-medium">{fmtNum(value)}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
      <Activity className="h-4 w-4 mr-2" />
      {message}
    </div>
  );
}
